import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';

/**
 * Поща — връзката към Gmail.
 *
 * Паролата за приложение се въвежда тук, а не се дава на никого и не се
 * пише в кода: от полето отива право на сървъра, записва се във файл,
 * четим само от него, и оттам нататък не се връща обратно. Екранът
 * показва единствено дали е сложена.
 *
 * Затова и няма поле „покажи паролата": ако някой я е забравил, прави се
 * нова от Google, а старата се отменя.
 */
export default function MailSettings() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const load = () => api.get('/api/mail')
    .then(setStatus)
    .catch((err) => setError(err.message));

  useEffect(() => { load(); }, []);

  // Т. 19: одиторът вижда състоянието на пощата, но не го пипа.
  const canWrite = user.role === 'admin';

  async function run(what) {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      if (what === 'save') {
        const res = await api.put('/api/mail/password', { password });
        setStatus(res);
        setPassword('');
        setDone('Паролата е записана.');
      } else if (what === 'open' || what === 'lock') {
        const res = await api.put('/api/mail/mode', { testing: what === 'lock' });
        setStatus(res);
        setDone(what === 'lock'
          ? 'Пощата е в пробен режим — писмата се връщат при нас.'
          : 'Пощата е отворена. Следващото напомняне тръгва към клиента.');
      } else {
        const res = await api.post('/api/mail/test', { send: what === 'send' });
        setStatus(res.status);
        setDone(what === 'send'
          ? `Писмото тръгна към ${res.to}.${res.redirected ? ' (пробен режим — не излиза навън)' : ''}`
          : 'Връзката и паролата минават.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="card-head">Поща</div>
      <div className="card-body">
        <p className="hint" style={{ marginTop: 0 }}>
          Писмата тръгват от адреса по-долу — отговорите идват в същата поща,
          а копие остава в „Изпратени".
        </p>

        <table className="inner" style={{ marginBottom: 14 }}>
          <tbody>
            <tr>
              <td className="muted" style={{ width: 130 }}>Сървър</td>
              <td>{status ? `${status.host}:${status.port}` : '…'}</td>
            </tr>
            <tr>
              <td className="muted">Адрес</td>
              <td>{status?.user ?? '—'}</td>
            </tr>
            <tr>
              <td className="muted">Подател</td>
              <td>{status?.from ?? '—'}</td>
            </tr>
            <tr>
              <td className="muted">Изпращане</td>
              <td>
                {status?.testing
                  ? <span className="badge">пробно — писмата се връщат при нас</span>
                  : <span className="badge ok">отворено — писмата тръгват към клиентите</span>}
                {status?.followsSystem && (
                  <span className="hint" style={{ marginLeft: 8 }}>
                    следва общия пробен режим
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <td className="muted">Парола</td>
              <td>
                {status?.hasPassword
                  ? <span className="badge ok">сложена</span>
                  : <span className="badge danger">липсва</span>}
              </td>
            </tr>
          </tbody>
        </table>

        {error && !canWrite && <div className="error">{error}</div>}

        {!canWrite && (
          <div className="note" style={{ marginBottom: 0 }}>
            Пощата се настройва от администратор — тук е само за преглед.
          </div>
        )}

        {canWrite && (
          <>
            <label className="field" style={{ maxWidth: 320 }}>
              Парола за приложение от Google
              <input
                type="password"
                autoComplete="off"
                value={password}
                placeholder="16 знака"
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <span className="hint">
              Прави се в профила на Google: Сигурност → Потвърждаване в две стъпки →
              Пароли за приложения. Интервалите между знаците не пречат.
            </span>

            {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
            {done && <div className="note" style={{ marginTop: 12 }}>{done}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
              <button className="primary" disabled={busy || !password.trim()}
                onClick={() => run('save')}>
                Запиши паролата
              </button>
              <button disabled={busy || !status?.hasPassword} onClick={() => run('verify')}>
                Провери връзката
              </button>
              <button disabled={busy || !status?.hasPassword} onClick={() => run('send')}>
                Изпрати пробно писмо
              </button>
              {/*
                * Отварянето е решение с последствия: оттам нататък напомнянията
                * стигат до истинските адреси на клиентите. Затова стои отделно
                * и казва какво прави, а не „включи/изключи".
                */}
              {status?.testing
                ? (
                  <button disabled={busy} onClick={() => run('open')}>
                    Отвори изпращането навън
                  </button>
                )
                : (
                  <button disabled={busy} onClick={() => run('lock')}>
                    Върни в пробен режим
                  </button>
                )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
